import React, { createContext, useState, useMemo } from 'react';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);

    // Function to add an item to the cart
    const addToCart = (item) => {
        setCartItems((prevItems) => {
            const existingItem = prevItems.find((cartItem) => cartItem.product_id === item.product_id);
            if (existingItem) {
                return prevItems.map((cartItem) =>
                    cartItem.product_id === item.product_id
                        ? {
                            ...cartItem,
                            quantity: cartItem.quantity + item.quantity, // Update quantity
                            vendor_id: item.vendor_id || cartItem.vendor_id, // Ensure vendor_id is set or updated
                            // Any other fields that might need updating can be added here
                        }
                        : cartItem
                );
            } else {
                return [
                    ...prevItems,
                    {
                        product_id: item.product_id, // Set product_id when adding the item
                        vendor_id: item.vendor_id,   // Set vendor_id when adding the item
                        name: item.name,
                        price: item.price,
                        quantity: item.quantity,
                        image: item.image,
                        stock: item.stock,
                        delivery_charge: item.delivery_charge,
                        // Add any additional fields needed
                    },
                ];
            }
        });
    };

    // Function to update the quantity of an item in the cart
    const updateCartItemQuantity = (product_id, quantity) => {
        setCartItems((prevItems) =>
            prevItems.map((item) =>
                item.product_id === product_id ? { ...item, quantity: Math.max(quantity, 1) } : item
            )
        );
    };

    // Function to remove an item from the cart
    const removeFromCart = (product_id) => {
        setCartItems((prevItems) => prevItems.filter((item) => item.product_id !== product_id));
    };

    // Function to clear the entire cart
    const clearCart = () => {
        setCartItems([]);
    };

    // Memoized function to calculate the total price
    const totalPrice = useMemo(() => {
        return cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
    }, [cartItems]);

    // Memoized function to calculate the delivery charges based on vendors
    const deliveryCharges = useMemo(() => {
        const groupedByVendor = cartItems.reduce((acc, item) => {
            if (!acc[item.vendor_id]) {
                acc[item.vendor_id] = [];
            }
            acc[item.vendor_id].push(item);
            return acc;
        }, {});

        return Object.values(groupedByVendor).reduce((total, items) => {
            const maxDeliveryCharge = Math.max(...items.map(item => item.delivery_charge || 0));
            return total + maxDeliveryCharge;
        }, 0);
    }, [cartItems]);

    // Provide context value
    const cartValue = {
        cartItems,
        addToCart,
        updateCartItemQuantity,
        removeFromCart,
        clearCart,
        totalPrice,
        deliveryCharges,
    };

    return (
        <CartContext.Provider value={cartValue}>
            {children}
        </CartContext.Provider>
    );
};
